(() => {
  if (customElements.get('cgl-bracelet-customizer')) return;

  class CglBraceletCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.assets = this.parseJson('[data-cgl-bc-assets]');
      this.variantId = this.dataset.variantId;
      this.price = Number(this.dataset.price || 0);
      this.color = this.dataset.defaultColor === 'Silver' ? 'Silver' : 'Gold';
      this.positions = ['Left','Center','Right'];
      this.windows = [
        { x:.08375,y:.37875,w:.18625,h:.17 },
        { x:.389375,y:.6075,w:.194375,h:.17625 },
        { x:.71,y:.394375,w:.18,h:.166875 }
      ];
      this.photos = this.positions.map(() => this.emptyPhoto());
      this.activeIndex = null;
      this.pointers = new Map();
      this.loading = false;
      this.customizationId = this.makeId();
      this.imageCache = new Map();
      this.debug = new URLSearchParams(location.search).get('cgl_debug') === '1';
      this.sessionId = this.getSessionId();

      this.overlay = this.querySelector('[data-cgl-bc-overlay]');
      this.stage = this.querySelector('[data-cgl-bc-stage]');
      this.previewPanel = this.querySelector('[data-cgl-bc-preview-panel]');
      this.productIntro = this.querySelector('[data-cgl-bc-product-intro]');
      this.editorAction = this.querySelector('[data-cgl-bc-editor-action]');
      this.gesture = this.querySelector('[data-cgl-bc-gesture]');
      this.resetButton = this.querySelector('[data-cgl-bc-reset]');
      this.confirmButton = this.querySelector('[data-cgl-bc-confirm-photo]');
      this.recapCanvas = this.querySelector('[data-cgl-bc-recap-canvas]');
      this.dockCanvas = this.querySelector('[data-cgl-bc-dock-canvas]');
      this.naturalBuy = this.querySelector('[data-cgl-bc-natural-buy]');
      this.stickyBuy = this.querySelector('[data-cgl-bc-sticky-buy]');
      this.progress = this.querySelector('[data-cgl-bc-progress]');
      this.progressFill = this.progress?.querySelector('span');
      this.formError = this.querySelector('[data-cgl-bc-form-error]');
      this.photoError = this.querySelector('[data-cgl-bc-photo-error]');

      this.bind();
      this.updateColor();
      this.update();
      this.initDock();
      this.initStickyBuy();
      this.track('customizer_view');
    }

    disconnectedCallback() {
      this.photos.forEach((photo) => photo.url && URL.revokeObjectURL(photo.url));
      window.removeEventListener('scroll',this.onScroll);
      window.removeEventListener('resize',this.onScroll);
      this.buyObserver?.disconnect();
    }

    emptyPhoto() {
      return { file:null,production:null,url:'',image:null,confirmed:false,adjusted:false,zoom:1,x:0,y:0,aspect:1 };
    }

    bind() {
      this.querySelectorAll('[data-cgl-bc-color]').forEach((button) => button.addEventListener('click',() => {
        this.color = button.dataset.cglBcColor;
        this.updateColor();
        this.update();
        this.track('color_selected',{ color:this.color });
      }));
      this.querySelectorAll('[data-cgl-bc-slot]').forEach((button) => button.addEventListener('click',() => this.openSlot(Number(button.dataset.cglBcSlot))));
      this.querySelectorAll('[data-cgl-bc-input]').forEach((input) => input.addEventListener('change',(event) => this.receiveFile(Number(input.dataset.cglBcInput),event.target.files?.[0])));
      this.confirmButton?.addEventListener('click',() => this.confirmPhoto());
      this.resetButton?.addEventListener('click',() => this.resetActivePhoto());
      this.querySelector('[data-cgl-bc-dock-preview]')?.addEventListener('click',() => this.previewPanel?.scrollIntoView({ behavior:'smooth',block:'start' }));
      this.querySelectorAll('[data-cgl-bc-atc]').forEach((button) => button.addEventListener('click',() => this.addToCart()));
      this.stage?.addEventListener('pointerdown',(event) => this.pointerDown(event));
      this.stage?.addEventListener('pointermove',(event) => this.pointerMove(event));
      ['pointerup','pointercancel','pointerleave'].forEach((type) => this.stage?.addEventListener(type,(event) => this.pointerUp(event)));
    }

    parseJson(selector) {
      try { return JSON.parse(this.querySelector(selector)?.textContent || '{}'); }
      catch (error) { return {}; }
    }

    openSlot(index) {
      if (!Number.isInteger(index) || !this.photos[index]) return;
      this.hideError(this.photoError);
      if (!this.photos[index].file) {
        this.track('photo_upload_started',{ photo_position:this.positions[index].toLowerCase() });
        this.querySelector(`[data-cgl-bc-input="${index}"]`)?.click();
        return;
      }
      this.startEditing(index);
    }

    async receiveFile(index,file) {
      const input = this.querySelector(`[data-cgl-bc-input="${index}"]`);
      if (input) input.value = '';
      if (!file) return;
      if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name || '')) return this.showError(this.photoError,'Please choose a JPG, PNG, WebP, HEIC or HEIF photo.');
      const old = this.photos[index];
      if (old.url) URL.revokeObjectURL(old.url);
      const photo = this.emptyPhoto();
      photo.file = file;
      photo.url = URL.createObjectURL(file);
      this.photos[index] = photo;
      try {
        photo.image = await this.loadImage(photo.url,false);
        photo.aspect = photo.image.naturalWidth / Math.max(1,photo.image.naturalHeight);
        photo.productionPromise = this.optimizePhoto(file,photo.image).then((optimized) => { photo.production = optimized; return optimized; });
        this.track('photo_uploaded',{ photo_position:this.positions[index].toLowerCase(), photos_uploaded:this.photos.filter((item) => item.file).length });
        this.update();
        this.startEditing(index);
      } catch (error) {
        this.showError(this.photoError,'This photo could not be previewed. Please try a JPG, PNG or WebP image.');
      }
    }

    startEditing(index) {
      this.activeIndex = index;
      this.dataset.editingSlot = String(index);
      this.photos[index].confirmed = false;
      this.classList.add('is-photo-editing');
      document.documentElement.classList.add('cgl-bc-editor-open');
      this.editorAction.hidden = false;
      this.resetButton.hidden = false;
      this.gesture.hidden = this.photos[index].adjusted;
      this.querySelector('[data-cgl-bc-confirm-label]').textContent = `USE ${this.positions[index].toUpperCase()} PHOTO`;
      this.querySelectorAll('[data-cgl-bc-slot]').forEach((slot) => slot.classList.toggle('is-active',Number(slot.dataset.cglBcSlot) === index));
      this.querySelectorAll('[data-cgl-bc-window]').forEach((windowEl) => windowEl.classList.toggle('is-active',Number(windowEl.dataset.cglBcWindow) === index));
      this.updatePhotoTransforms();
      requestAnimationFrame(() => this.previewPanel?.scrollIntoView({ behavior:'smooth',block:'start' }));
    }

    confirmPhoto() {
      if (this.activeIndex === null) return;
      const index = this.activeIndex;
      this.photos[index].confirmed = true;
      this.activeIndex = null;
      delete this.dataset.editingSlot;
      this.classList.remove('is-photo-editing');
      document.documentElement.classList.remove('cgl-bc-editor-open');
      this.editorAction.hidden = true;
      this.resetButton.hidden = true;
      this.gesture.hidden = true;
      this.querySelectorAll('.is-active').forEach((element) => element.classList.remove('is-active'));
      this.track('photo_confirmed',{ photo_position:this.positions[index].toLowerCase(), photos_confirmed:this.confirmedCount() });
      this.update();
      const next = this.photos.findIndex((photo) => !photo.confirmed);
      if (next >= 0) requestAnimationFrame(() => this.querySelector(`[data-cgl-bc-slot="${next}"]`)?.scrollIntoView({ behavior:'smooth',block:'center' }));
      else this.querySelector('[data-cgl-bc-recap]')?.scrollIntoView({ behavior:'smooth',block:'start' });
    }

    resetActivePhoto() {
      if (this.activeIndex === null) return;
      Object.assign(this.photos[this.activeIndex],{ zoom:1,x:0,y:0,adjusted:false });
      this.gesture.hidden = false;
      this.updatePhotoTransforms();
    }

    updateColor() {
      this.querySelectorAll('[data-cgl-bc-color]').forEach((button) => {
        const selected = button.dataset.cglBcColor === this.color;
        button.classList.toggle('is-selected',selected);
        button.setAttribute('aria-checked',String(selected));
      });
      const source = this.assets[this.color] || this.assets.Gold;
      if (source && this.overlay?.getAttribute('src') !== source) this.overlay.src = source;
      this.setText('[data-cgl-bc-dock-detail]',`${this.color} · Three photos · 7" + 2"`);
      this.setText('[data-cgl-bc-recap-detail]',`${this.color} · 7" + 2" extender`);
      this.renderPreviewCanvases();
    }

    update() {
      this.photos.forEach((photo,index) => {
        const windowEl = this.querySelector(`[data-cgl-bc-window="${index}"]`);
        const preview = this.querySelector(`[data-cgl-bc-photo="${index}"]`);
        const slot = this.querySelector(`[data-cgl-bc-slot="${index}"]`);
        const slotImage = this.querySelector(`[data-cgl-bc-slot-image="${index}"]`);
        windowEl?.classList.toggle('has-photo',Boolean(photo.url));
        slot?.classList.toggle('has-photo',Boolean(photo.url));
        slot?.classList.toggle('is-complete',photo.confirmed);
        if (preview && photo.url && preview.src !== photo.url) preview.src = photo.url;
        if (slotImage && photo.url && slotImage.src !== photo.url) slotImage.src = photo.url;
        this.setText(`[data-cgl-bc-slot-label="${index}"]`,photo.confirmed ? 'Tap to edit' : photo.file ? 'Finish photo' : 'Add photo');
      });
      const count = this.confirmedCount();
      this.setText('[data-cgl-bc-preview-status]',count === 3 ? 'All three photos ready' : `${count} of 3 photos ready`);
      const ready = count === 3;
      this.querySelectorAll('[data-cgl-bc-atc]').forEach((button) => { button.disabled = !ready || this.loading; button.setAttribute('aria-disabled',String(!ready)); });
      const buyStatus = this.querySelector('[data-cgl-bc-buy-status]');
      if (buyStatus) {
        buyStatus.hidden = ready;
        buyStatus.textContent = ready ? '' : `Add ${3 - count} more photo${3 - count === 1 ? '' : 's'} to continue`;
      }
      this.setText('[data-cgl-bc-delivery-date]',this.deliveryEstimate());
      this.updatePhotoTransforms();
      this.renderPreviewCanvases();
      this.updateStickyBuy?.();
      if (ready) this.trackOnce('customizer_completed');
    }

    confirmedCount() { return this.photos.filter((photo) => photo.confirmed).length; }
    isReady() { return this.confirmedCount() === 3 && Boolean(this.variantId); }

    pointerDown(event) {
      if (this.activeIndex === null) return;
      event.preventDefault();
      this.stage.setPointerCapture?.(event.pointerId);
      this.pointers.set(event.pointerId,{ x:event.clientX,y:event.clientY });
      if (this.pointers.size === 2) {
        const points = [...this.pointers.values()];
        this.pinch = { distance:this.distance(points[0],points[1]),zoom:this.photos[this.activeIndex].zoom };
      }
    }

    pointerMove(event) {
      if (this.activeIndex === null || !this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      const previous = this.pointers.get(event.pointerId);
      this.pointers.set(event.pointerId,{ x:event.clientX,y:event.clientY });
      const photo = this.photos[this.activeIndex];
      if (this.pointers.size >= 2) {
        const points = [...this.pointers.values()];
        photo.zoom = this.clamp(this.pinch.zoom * this.distance(points[0],points[1]) / Math.max(1,this.pinch.distance),.25,5);
      } else {
        const windowEl = this.querySelector(`[data-cgl-bc-window="${this.activeIndex}"]`);
        const rect = windowEl?.getBoundingClientRect();
        if (rect) {
          photo.x = this.clamp(photo.x + (event.clientX - previous.x) / Math.max(1,rect.width),-4,4);
          photo.y = this.clamp(photo.y + (event.clientY - previous.y) / Math.max(1,rect.height),-4,4);
        }
      }
      if (!photo.adjusted) this.track('photo_adjusted',{ photo_position:this.positions[this.activeIndex].toLowerCase() });
      photo.adjusted = true;
      this.gesture.hidden = true;
      this.updatePhotoTransforms();
    }

    pointerUp(event) {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinch = null;
    }

    updatePhotoTransforms() {
      this.photos.forEach((photo,index) => {
        const image = this.querySelector(`[data-cgl-bc-photo="${index}"]`);
        const frame = this.querySelector(`[data-cgl-bc-window="${index}"]`);
        if (!image || !frame) return;
        const imageAspect = Number(photo.aspect || (image.naturalWidth / Math.max(1,image.naturalHeight)) || 1);
        const fallbackFrame = this.windows[index] || { w:1,h:1 };
        const frameAspect = frame.clientWidth && frame.clientHeight
          ? frame.clientWidth / frame.clientHeight
          : fallbackFrame.w / Math.max(.0001,fallbackFrame.h);
        if (imageAspect >= frameAspect) {
          image.style.width = `${(imageAspect / frameAspect) * 100}%`;
          image.style.height = '100%';
        } else {
          image.style.width = '100%';
          image.style.height = `${(frameAspect / imageAspect) * 100}%`;
        }
        image.style.left = `calc(50% + ${photo.x * 100}%)`;
        image.style.top = `calc(50% + ${photo.y * 100}%)`;
        image.style.transform = `translate(-50%,-50%) scale(${photo.zoom})`;
      });
    }

    async renderPreviewCanvases() {
      const token = Symbol('render');
      this.renderToken = token;
      try {
        const overlay = await this.loadImage(this.assets[this.color] || this.assets.Gold);
        const render = (canvas) => {
          if (!canvas || this.renderToken !== token) return;
          const context = canvas.getContext('2d');
          if (!context) return;
          const size = canvas.width;
          context.fillStyle = '#1d1b20';
          context.fillRect(0,0,size,size);
          this.photos.forEach((photo,index) => {
            if (!photo.image) return;
            const geometry = this.windows[index];
            const x = geometry.x * size,y = geometry.y * size,w = geometry.w * size,h = geometry.h * size;
            context.save(); context.beginPath(); context.rect(x,y,w,h); context.clip();
            this.drawCover(context,photo.image,x,y,w,h,photo); context.restore();
          });
          context.drawImage(overlay,0,0,size,size);
        };
        render(this.recapCanvas); render(this.dockCanvas);
      } catch (error) { if (this.debug) console.warn('[CustomGLD bracelet] preview render failed',error); }
    }

    drawCover(context,image,x,y,width,height,state) {
      const scale = Math.max(width / image.naturalWidth,height / image.naturalHeight) * state.zoom;
      const drawWidth = image.naturalWidth * scale,drawHeight = image.naturalHeight * scale;
      context.drawImage(image,x + (width - drawWidth) / 2 + state.x * width,y + (height - drawHeight) / 2 + state.y * height,drawWidth,drawHeight);
    }

    async addToCart() {
      if (this.loading) return;
      if (!this.isReady()) {
        const next = this.photos.findIndex((photo) => !photo.confirmed);
        this.showError(this.photoError,'Please upload and confirm all three photos.');
        this.querySelector(`[data-cgl-bc-slot="${Math.max(0,next)}"]`)?.scrollIntoView({ behavior:'smooth',block:'center' });
        return;
      }
      this.loading = true;
      this.classList.add('is-loading');
      this.hideError(this.formError);
      this.setText('[data-cgl-bc-buy-status]','Preparing your bracelet…');
      this.setProgress(5);
      this.track('add_to_cart_clicked');
      try {
        const [preview,...production] = await Promise.all([this.makePreviewFile(),...this.photos.map((photo) => photo.productionPromise || photo.production || photo.file)]);
        const form = new FormData();
        form.append('id',this.variantId);
        form.append('quantity','1');
        form.append('properties[Color]',this.color);
        form.append('properties[Bracelet length]','7" + 2" extender');
        form.append('properties[Estimated delivery]',this.deliveryEstimate());
        form.append('properties[Preview]',preview,preview.name);
        production.forEach((file,index) => form.append(`properties[_${this.positions[index]} Photo]`,file,file.name));
        form.append('properties[_Customizer Version]','Bracelet V2');
        form.append('properties[_Customization ID]',this.customizationId);
        form.append('sections','cart-drawer,cart-icon-bubble');
        form.append('sections_url',location.pathname);
        const result = await this.upload(form);
        this.setProgress(100);
        this.setText('[data-cgl-bc-buy-status]','✓ Your bracelet is in the bag');
        this.track('add_to_cart_success');
        this.refreshCart(result);
        window.setTimeout(() => { location.href = `${window.Shopify?.routes?.root || '/'}cart`; },350);
      } catch (error) {
        this.showError(this.formError,error.message || 'Something went wrong. Please try again.');
        this.track('add_to_cart_error',{ error_message:String(error.message || '').slice(0,80) });
        this.loading = false;
        this.classList.remove('is-loading');
      }
    }

    async makePreviewFile() {
      await this.renderPreviewCanvases();
      const blob = await new Promise((resolve,reject) => this.recapCanvas.toBlob((value) => value ? resolve(value) : reject(new Error('Could not create your bracelet preview.')),'image/jpeg',.84));
      return new File([blob],`customgld-bracelet-preview-${Date.now()}.jpg`,{ type:'image/jpeg' });
    }

    async optimizePhoto(file,image) {
      const maxEdge = Math.max(image.naturalWidth,image.naturalHeight);
      if (file.size <= 2.5 * 1024 * 1024 && maxEdge <= 2400) return file;
      const scale = Math.min(1,2400 / maxEdge);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.naturalWidth * scale); canvas.height = Math.round(image.naturalHeight * scale);
      const context = canvas.getContext('2d',{ alpha:false });
      if (!context) return file;
      context.fillStyle = '#fff'; context.fillRect(0,0,canvas.width,canvas.height); context.drawImage(image,0,0,canvas.width,canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve,'image/jpeg',.88));
      canvas.width = canvas.height = 1;
      return blob ? new File([blob],`${this.cleanName(file.name)}-print.jpg`,{ type:'image/jpeg' }) : file;
    }

    upload(form) {
      return new Promise((resolve,reject) => {
        const request = new XMLHttpRequest();
        request.open('POST',`${window.Shopify?.routes?.root || '/'}cart/add.js`);
        request.setRequestHeader('X-Requested-With','XMLHttpRequest');
        request.responseType = 'json';
        request.upload.addEventListener('progress',(event) => {
          if (!event.lengthComputable) return;
          this.setProgress(Math.max(5,Math.round(event.loaded / event.total * 92)));
          this.setText('[data-cgl-bc-buy-status]',event.loaded / event.total < .55 ? 'Saving your photos…' : 'Finishing your bracelet…');
        });
        request.addEventListener('load',() => request.status >= 200 && request.status < 300 ? resolve(request.response || {}) : reject(new Error(request.response?.description || 'Could not add this bracelet to your cart.')));
        request.addEventListener('error',() => reject(new Error('Your connection dropped. Please try again.')));
        request.send(form);
      });
    }

    refreshCart(result) {
      const sections = result?.sections || {};
      Object.entries(sections).forEach(([id,html]) => {
        const current = document.getElementById(id);
        if (!current || !html) return;
        const parsed = new DOMParser().parseFromString(html,'text/html').getElementById(id);
        if (parsed) current.replaceWith(parsed);
      });
    }

    initDock() {
      const update = () => {
        this.updatePhotoTransforms();
        const boundary = this.productIntro?.getBoundingClientRect().bottom ?? this.getBoundingClientRect().top;
        const visible = !this.classList.contains('is-photo-editing') && boundary <= 0;
        this.classList.toggle('is-dock-visible',visible);
        this.querySelector('[data-cgl-bc-dock]')?.setAttribute('aria-hidden',String(!visible));
      };
      this.onScroll = () => requestAnimationFrame(update);
      window.addEventListener('scroll',this.onScroll,{ passive:true });
      window.addEventListener('resize',this.onScroll,{ passive:true });
      update();
    }

    initStickyBuy() {
      if (!this.naturalBuy || !this.stickyBuy || !('IntersectionObserver' in window)) return;
      let seen = false;
      let lastEntry = null;
      this.updateStickyBuy = () => {
        if (!lastEntry) return;
        const visible = this.isReady() && seen && !lastEntry.isIntersecting && lastEntry.boundingClientRect.bottom < 0;
        this.stickyBuy.classList.toggle('is-visible',visible);
        this.stickyBuy.setAttribute('aria-hidden',String(!visible));
      };
      this.buyObserver = new IntersectionObserver((entries) => {
        lastEntry = entries[0];
        if (lastEntry.isIntersecting) seen = true;
        this.updateStickyBuy();
      },{ threshold:.1 });
      this.buyObserver.observe(this.naturalBuy);
    }

    loadImage(source,anonymous = true) {
      if (!source) return Promise.reject(new Error('Bracelet overlay is missing.'));
      const key = `${anonymous ? 'cors' : 'local'}:${source}`;
      if (this.imageCache.has(key)) return this.imageCache.get(key);
      const promise = new Promise((resolve,reject) => {
        const image = new Image();
        if (anonymous) image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image); image.onerror = reject; image.src = source;
      });
      this.imageCache.set(key,promise); return promise;
    }

    deliveryEstimate() {
      const min = Number(this.dataset.deliveryMin || 17),max = Number(this.dataset.deliveryMax || 21);
      const format = (date) => date.toLocaleDateString('en-US',{ month:'short',day:'numeric' });
      const first = new Date(); first.setDate(first.getDate() + min);
      const last = new Date(); last.setDate(last.getDate() + max);
      return `${format(first)}–${format(last)}`;
    }

    track(name,extra = {}) {
      const eventName = name.startsWith('cgl_') ? name : `cgl_bracelet_${name}`;
      const payload = { cgl_session_id:this.sessionId,cgl_customization_id:this.customizationId,product_id:this.dataset.productId,variant_id:this.variantId,color:this.color,photos_uploaded:this.photos.filter((photo) => photo.file).length,photos_confirmed:this.confirmedCount(),price:this.price / 100,currency:window.Shopify?.currency?.active || 'USD',occurred_at:new Date().toISOString(),...extra };
      if (this.debug) console.info(`[CustomGLD] ${eventName}`,payload);
      document.dispatchEvent(new CustomEvent('cgl:bracelet-customizer',{ detail:{ event_name:eventName,...payload } }));
      try { window.Shopify?.analytics?.publish?.(eventName,payload); } catch (error) {}
      try { if (typeof window.gtag === 'function') window.gtag('event',eventName,payload); else if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event:eventName,...payload }); } catch (error) {}
      try { window.clarity?.('event',eventName); } catch (error) {}
    }

    trackOnce(name,extra = {}) { this.tracked ||= new Set(); if (this.tracked.has(name)) return; this.tracked.add(name); this.track(name,extra); }
    getSessionId() { try { const key='cgl_session_id_v1'; let id=sessionStorage.getItem(key); if (!id) { id=this.makeId('cgls'); sessionStorage.setItem(key,id); } return id; } catch (error) { return this.makeId('cgls'); } }
    makeId(prefix='cglb') { return `${prefix}_${typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`}`; }
    cleanName(name='photo') { return name.replace(/\.[^.]+$/,'').replace(/[^a-z0-9_-]+/gi,'-').slice(0,60); }
    distance(a,b) { return Math.hypot(a.x-b.x,a.y-b.y); }
    clamp(value,min,max) { return Math.min(max,Math.max(min,value)); }
    setText(selector,text) { const element=this.querySelector(selector); if (element) element.textContent=text; }
    setProgress(value) { if (this.progressFill) this.progressFill.style.width=`${value}%`; this.progress?.setAttribute('aria-valuenow',String(value)); }
    showError(element,message) { if (!element) return; element.textContent=message; element.hidden=false; }
    hideError(element) { if (!element) return; element.hidden=true; element.textContent=''; }
  }

  customElements.define('cgl-bracelet-customizer',CglBraceletCustomizer);
})();